import {
  AssetStatus,
  createAssetFormSchema,
  toAssetRequest,
  type CreateAssetFormValues,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AssetFields } from '@/products/helpdesk/features/assets/asset-fields';
import { useAssetLookups, useCreateAsset } from '@/products/helpdesk/features/assets/use-assets';
import { ApiRequestError } from '@/lib/api';
import { useVoiceCopy } from '@/platform/voice/use-voice';

const EMPTY: CreateAssetFormValues = {
  name: '',
  assetTypeId: '',
  status: AssetStatus.IN_SERVICE,
};

export function AssetCreatePage() {
  const navigate = useNavigate();
  const lookups = useAssetLookups();
  const createAsset = useCreateAsset();
  const copy = useVoiceCopy();
  const [values, setValues] = useState<CreateAssetFormValues>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);

  function patch(next: Partial<CreateAssetFormValues>) {
    setValues((current) => ({ ...current, ...next }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = createAssetFormSchema.safeParse(values);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? copy('helpdesk.form.check'));
      return;
    }
    setFormError(null);
    try {
      const created = await createAsset.mutateAsync(toAssetRequest(parsed.data));
      toast.success('Asset angelegt.');
      await navigate(`/helpdesk/assets/${created.id}`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Asset konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2">
          <Link to="/helpdesk/assets">← Zurück zur Übersicht</Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Neues Asset</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gerät, Lizenz oder Netzwerkkomponente erfassen.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stammdaten</CardTitle>
          <CardDescription>Pflichtfelder sind Bezeichnung und Typ.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={(event) => void onSubmit(event)}>
            <AssetFields values={values} lookups={lookups.data} onChange={patch} />
            {formError && (
              <p role="alert" className="text-sm text-destructive">
                {formError}
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={createAsset.isPending}>
                {createAsset.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
                Anlegen
              </Button>
              <Button asChild type="button" variant="outline">
                <Link to="/helpdesk/assets">Abbrechen</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
